import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import Header from '../../layouts/Header';
import { ArrowLeft, Download, FileEarmarkSpreadsheet, Printer } from 'react-bootstrap-icons';
import { Button, Col, Form, Row, Spinner, Table, Card, Badge } from 'react-bootstrap';

const ProductStockReport = () => {
    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);
    const navigate = useNavigate();

    const accessToken = localStorage.getItem('authToken');
    const [outlets, setOutlets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedOutlet, setSelectedOutlet] = useState('');
    const [selectedOutletName, setSelectedOutletName] = useState('');
    const [reportData, setReportData] = useState([]);
    const [outletName, setOutletName] = useState('');
    const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);

    const getOutlets = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("outlet", { headers });
            if (response?.data?.status === true) {
                const activeOutlets = response?.data?.data?.outlets.filter(outlet => outlet?.status === "ACTIVE" || "INACTIVE");
                setOutlets(activeOutlets);
            }
        } catch (error) {
            console.error("Error fetching outlets:", error);
            toast.error("Failed to fetch outlets", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedOutlet) return;
        
        setLoading(true);
        try {
            await getProductStock(selectedOutlet);
        } finally {
            setLoading(false);
        }
    };

    const getProductStock = async (outletId, download) => {
        try {
            const headers = { 
                Authorization: `Bearer ${accessToken}` 
            };
            let newUrl = `product/getExhaustedProducts?outlet=${outletId}`;
            if(download) {
                newUrl += `&isDownload=${download}`
            }
            const response = await axios.get(newUrl, { 
                headers,
                responseType: download ? 'blob' : 'json' 
            });
            console.log("Response :", response);

            if (download) {
                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
        
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = `${selectedOutletName} Out of Stock Report.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
        
                toast.success('Excel file downloaded successfully!', {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light"
                });
            } else {
                console.log("Response :", response);
                
                setReportData(response.data.data.exhaustedProducts);
                // Find the outlet name for display
                const outlet = outlets.find(o => o._id === outletId);
                setOutletName(outlet?.name || 'Selected Outlet');
                toast.success(response.data.data.message, {
                    position: "top-right",
                    autoClose: 2000,
                    theme: "colored",
                });
            }
            
            // if (response?.data?.status === true) {
                
            // }
        } catch (error) {
            console.error("Error fetching product stock:", error);
            let errorMessage;
            
            if (error?.response) {
                if (error.response.status === 400) {
                    errorMessage = error.response.data.data?.message || error.response.data.title;
                } else if (error.response.status === 404) {
                    errorMessage = error?.response?.data?.data?.message || "Resource not found";
                } else if (error.response.status === 406) {
                    errorMessage = error?.response?.data?.data?.error?.message || error.response.data.error;
                } else if (error.response.status === 412) {
                    errorMessage = error?.response?.data?.data?.message || error.response.data.error;
                } else if (error.response.status === 500) {
                    errorMessage = error?.response?.data?.data?.error || "Server error";
                }
            }
            setReportData([]);
            toast.error(errorMessage, {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            });
        } finally {
            setDownloadBtnLoading(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const handleExport = () => {
        // Implement export functionality (CSV/Excel)
        // toast.info("Export functionality will be implemented soon", {
        //     position: "top-right",
        //     autoClose: 2000,
        // });
        setDownloadBtnLoading(true);
        // const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
        // const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
        getProductStock(selectedOutlet, true);

    };

    useEffect(() => {
        getOutlets();
    }, []);

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div
                className={`main main-app p-3 p-lg-4 ${
                    skin === "dark" ? "bg-dark text-light" : "bg-light"
                }`}
            >
                <div className="d-flex flex-column gap-3">
                    {/* Header Card */}
                    <Card className="shadow-sm border-0">
                        <Card.Header className={`${skin === "dark" ? "bg-dark" : "bg-white"} border-0 py-3 d-flex justify-content-between align-items-center`}>
                            <div>
                                <h4 className="mb-0 fw-bold">
                                    <i className="bi bi-exclamation-triangle me-2"></i>OUT OF STOCK PRODUCTS REPORT
                                </h4>
                            </div>
                            <div>
                                <Button 
                                    variant="outline-secondary" 
                                    onClick={() => navigate('/reports/all-reports')}
                                    className="d-flex align-items-center fw-semibold"
                                >
                                    <ArrowLeft className="me-1" /> Back to Reports
                                </Button>
                            </div>
                        </Card.Header>
                    </Card>

                    {/* Filters Card */}
                    <Card className="shadow-sm border-0">
                        <Card.Body>
                            <Form onSubmit={handleSubmit}>
                                <Row className="g-3 align-items-end">
                                    <Col md={4} lg={3}>
                                        <Form.Group controlId="outletSelect">
                                            <Form.Label className="fw-semibold">
                                                <i className="bi bi-shop me-2"></i>Select Outlet <span className='text-danger'>*</span>
                                            </Form.Label>
                                            <Form.Select
                                                value={selectedOutlet}
                                                onChange={(e) => 
                                                    {
                                                        setSelectedOutlet(e.target.value);
                                                        setSelectedOutletName(e?.target?.selectedOptions[0]?.label)                                  
                                                    }
                                                }
                                                className="border-secondary"
                                                disabled={loading}
                                            >
                                                <option value="">Select Outlet</option>
                                                {outlets?.map((outlet) => (
                                                    <option key={outlet?._id} value={outlet?._id} label={`${outlet?.name} (${outlet?.city})`}>
                                                        {outlet?.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={3} lg={3}>
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            className="w-100"
                                            disabled={!selectedOutlet || loading}
                                        >
                                            {loading ? (
                                                <Spinner size="sm" animation="border" />
                                            ) : (
                                                "Generate Report"
                                            )}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>

                    {/* Report Card */}
                    {reportData.length > 0 && (
                        <Card className="shadow-sm border-0">
                            <Card.Header className={`${skin === "dark" ? "bg-dark" : "bg-white"} border-0 py-3 d-flex justify-content-between align-items-center`}>
                                <h5 className="mb-0 fw-bold">
                                    <i className="bi bi-file-earmark-text me-2"></i>
                                    Out of Stock Products - {outletName}
                                </h5>
                                <div className="w-25">
                                    {/* <Button variant="outline-secondary" size="sm" onClick={handlePrint}>
                                        <Printer className="me-1" /> Print
                                    </Button> */}
                                    <Button variant="outline-success fw-semibold w-100" size="sm" onClick={handleExport}>
                                        <FileEarmarkSpreadsheet className="me-1" />
                                        { downloadBtnLoading ? <Spinner size='sm' animation='border' /> : "Export Excel"}
                                    </Button>
                                </div>
                            </Card.Header>
                            <Card.Body>
                                <div className="table-responsive">
                                    <Table striped bordered hover className="mb-0">
                                        <thead className={`${skin === "dark" ? "table-dark" : "table-light"}`}>
                                            <tr>
                                                <th>#</th>
                                                <th>Category</th>
                                                <th>Product Name</th>
                                                <th>Description</th>
                                                <th>Type</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reportData.map((category, catIndex) => (
                                                <React.Fragment key={category._id}>
                                                    {category.products.map((product, prodIndex) => (
                                                        <tr key={`${catIndex}-${prodIndex}`}>
                                                            <td>{catIndex + 1}.{prodIndex + 1}</td>
                                                            <td>{catIndex === 0 || reportData[catIndex - 1].categoryName !== category.categoryName 
                                                                ? category.categoryName 
                                                                : ''}
                                                            </td>
                                                            <td className='text-primary fw-semibold'>{product.productName}</td>
                                                            <td className="text-muted" style={{ maxWidth: '300px' }}>
                                                                {product.description}
                                                            </td>
                                                            <td>
                                                                <Badge bg={product.classType === 'FOOD' ? 'info' : 'warning'}>
                                                                    {product.classType}
                                                                </Badge>
                                                            </td>
                                                            <td>
                                                                <Badge bg={product.status === 'ACTIVE' ? 'success' : 'secondary'}>
                                                                    {product.status}
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                {reportData.length === 0 && (
                                    <div className="text-center py-4">
                                        <h5>No out of stock products found</h5>
                                        <p className="text-muted">All products are currently in stock for the selected outlet</p>
                                    </div>
                                )}
                            </Card.Body>
                            {/* <Card.Footer className="text-muted">
                                <small>Report generated on {new Date().toLocaleString()}</small>
                            </Card.Footer> */}
                        </Card>
                    )}
                </div>
            </div>
        </React.Fragment>
    );
};

export default ProductStockReport;